import Moment from 'moment';

import TravelerAncillaryCollectionUIModel from './traveler-ancillary-collection';
import FreeBaggageAllowanceUIModel from './free-baggage-allowance';
import SegmentChoiceCollectionUIModel from './segment-choice-collection';
import MealsSelectedCollectionUIModel from './meals-selected-collection';
import BenefitRedeemedCollectionUIModel from './benefit-redeemed-collection';
import BenefitCorporateRecognitionUIModel from './benefit-corporate-recognition';
import SegmentChoiceUIModel from './segment-choice';
import SSRRemarksTransformer from './ssr-remarks';
import UserProfileAccountUIModel from './user-profile-account';

import { getInitialToggleValue } from '../utils';
import { isValidAgeForInsurance } from '../../scripts/utilities/helpers';

import SEGMENT_TYPES from '../../scripts/constants/segment-types-enum';
import travelerFormOptions from '../../scripts/constants/traveler-form-options';
import { dateOfBirth } from '../../scripts/validation/validation.js';

class TravelerInfoUIModel {
  constructor({
    ManageUserProfileAccount = undefined,
    allTicketNumbers = undefined,
    ancillaries = undefined,
    beneficiaryTier = null,
    benefit = undefined,
    benefitCorporateRecognition = undefined,
    benefitRedeemedCollection = undefined,
    bookingClasses = undefined,
    changeSeatAllowed = 0, // default hoursToExpire
    checkedBags = undefined,
    checkinNumber = 'CLOSED', // default manageStatus
    checkinStatus = '',
    corporateFrequentFlyerNumber = undefined,
    corporateFrequentFlyerProgram = null,
    countryOfResidence = undefined,
    couponCode = undefined,
    couponNumbers = undefined,
    ChubbInsuranceUSA = undefined,
    dateOfBirth = undefined,
    declinedEmergencyContact = undefined,
    destinationAddress = undefined,
    displayName = undefined,
    email = undefined,
    emergencyContact = undefined,
    etaAlreadySent = undefined,
    fareBasisCodes = undefined,
    firstName = undefined,
    freeBaggageAllowance = undefined,
    freeBaggageAllowancePerLegsArray = undefined,
    frequentFlyerNumber = undefined,
    frequentFlyerProgram = undefined,
    loyalty = undefined,
    gender = undefined,
    id = undefined,
    ineligibleReasons = undefined,
    infant = undefined,
    isEligibleToCheckin = undefined,
    isOverBookingEligible = undefined,
    isRedemptionBeneficiaryTier = false,
    isSelectedToCheckin = undefined,
    isSkyPriority = undefined,
    ktn = null,
    lastName = undefined,
    mealSelected = undefined,
    mealSelectionAllowed = undefined,
    middleName = undefined,
    nameRefNumber = undefined,
    onStandByList = undefined,
    passengerType = undefined,
    paxType = undefined,
    phones = undefined,
    redressNumber = null,
    savedEmails = undefined,
    segmentChoices = undefined,
    selectee = undefined,
    ssrRemarks = undefined,
    ticketNumber = undefined,
    timaticInfo = undefined,
    token = undefined,
    travelDocument = undefined,
    visaInfo = undefined,
    visaInfoAlreadySent = undefined,
  } = {}) {
    this.ManageUserProfileAccount = ManageUserProfileAccount ? new UserProfileAccountUIModel(ManageUserProfileAccount) : null;
    this.allTicketNumbers = allTicketNumbers;
    this.ancillaries = new TravelerAncillaryCollectionUIModel(ancillaries);
    this.beneficiaryTier = beneficiaryTier;
    this.benefit = benefit;
    this.benefitCorporateRecognition = benefitCorporateRecognition ? new BenefitCorporateRecognitionUIModel(benefitCorporateRecognition) : null;
    this.benefitRedeemedCollection = benefitRedeemedCollection ? new BenefitRedeemedCollectionUIModel(benefitRedeemedCollection) : benefitRedeemedCollection;
    this.bookingClasses = bookingClasses;
    this.changeSeatAllowed = changeSeatAllowed;
    this.checkedBags = checkedBags;
    this.checkinNumber = checkinNumber;
    this.checkinStatus = checkinStatus;
    this.corporateFrequentFlyerNumber = corporateFrequentFlyerNumber;
    this.corporateFrequentFlyerProgram = corporateFrequentFlyerProgram;
    this.countryOfResidence = countryOfResidence;
    this.couponCode = couponCode;
    this.couponNumbers = couponNumbers;
    this.ChubbInsuranceUSA = ChubbInsuranceUSA;
    this.dateOfBirth = dateOfBirth;
    this.declinedEmergencyContact = declinedEmergencyContact;
    this.destinationAddress = destinationAddress;
    this.displayName = displayName ? displayName : `${firstName} ${lastName}`;
    this.email = email;
    this.emergencyContact = emergencyContact;
    this.etaAlreadySent = etaAlreadySent;
    this.fareBasisCodes = fareBasisCodes;
    this.firstName = firstName;
    this.freeBaggageAllowance = freeBaggageAllowance ? new FreeBaggageAllowanceUIModel(freeBaggageAllowance) : freeBaggageAllowance;
    this.freeBaggageAllowancePerLegsArray = freeBaggageAllowancePerLegsArray || [];
    this.frequentFlyerNumber = frequentFlyerNumber;
    this.frequentFlyerProgram = frequentFlyerProgram;
    this.loyalty = loyalty;
    this.gender = gender;
    this.id = id;
    this.ineligibleReasons = ineligibleReasons;
    this.infant = infant;
    this.isEligibleToCheckin = isEligibleToCheckin;
    this.isOverBookingEligible = isOverBookingEligible;
    this.isRedemptionBeneficiaryTier = isRedemptionBeneficiaryTier;
    this.isSelectedToCheckin = isSelectedToCheckin;
    this.isSkyPriority = isSkyPriority;
    this.ktn = ktn;
    this.lastName = lastName;
    this.mealSelected = mealSelected ? new MealsSelectedCollectionUIModel(mealSelected) : mealSelected;
    this.mealSelectionAllowed = mealSelectionAllowed;
    this.middleName = middleName;
    this.nameRefNumber = nameRefNumber;
    this.onStandByList = onStandByList;
    this.passengerType = passengerType;
    this.paxType = paxType;
    this.phones = phones;
    this.redressNumber = redressNumber;
    this.savedEmails = savedEmails;
    this.segmentChoices = segmentChoices ? new SegmentChoiceCollectionUIModel(segmentChoices) : segmentChoices;
    this.selectee = selectee;
    this.ssrRemarks = ssrRemarks ? new SSRRemarksTransformer(ssrRemarks) : null;
    this.ticketNumber = ticketNumber;
    this.timaticInfo = timaticInfo;
    this.token = token;
    this.travelDocument = travelDocument;
    this.visaInfo = visaInfo;
    this.visaInfoAlreadySent = visaInfoAlreadySent;
    this.userId = '';
    this.concurFFNumber = '';
  }

  clearSessionCart() {
    this.ManageUserProfileAccount = null;
    this.ancillaries.getUnPaidAncillariesCP().forEach(ancillary => {
      ancillary.ancillary.redeemedCP = false;
    });
  }

  getAllUnPaidAncillaries() {
    return this.ancillaries.getAllUnPaidAncillaries();
  }

  getUnPaidAncillaries() {
    return this.ancillaries.getUnPaidAncillaries();
  }

  getUnPaiFlyGreenAncillaries() {
    return this.ancillaries.getUnPaiFlyGreenAncillaries();
  }

  getUnPaiPRIdAncillaries() {
    return this.ancillaries.getUnPaiPRIdAncillaries();
  }

  getUnPaidAncillariesCP() {
    return this.ancillaries.getUnPaidAncillariesCP();
  }

  getTicketedAncillaries() {
    return this.ancillaries.getTicketedAncillaries();
  }

  getTicketedAncillariesFlyGreen() {
    return this.ancillaries.getTicketedAncillariesFlyGreen();
  }

  getTicketedAncillariesPriority() {
    return this.ancillaries.getTicketedAncillariesPriority();
  }

  getTicketedAncillariesPP() {
    return this.ancillaries.getTicketedAncillariesPP();
  }

  getTicketedBaggagePP() {
    return this.ancillaries.getTicketedBaggagePP();
  }

  getTicketedUpgradeSegmentCodes() {
    return this.ancillaries.getTicketedUpgradeSegmentCodes();
  }

  getAllTicketedAncillaries() {
    return this.ancillaries.getAllTicketedAncillaries();
  }

  getAllTicketedCBX() {
    return this.ancillaries.getAllTicketedCBX();
  }

  getAllPaidAncillaries() {
    return this.ancillaries.getAllPaidAncillaries();
  }

  /**
   * @param  {object} initialFormData - hidden form data required for all pax
   * @return {object} initial form values for repopulating form
   */
  getManageTravelerInitialValues(initialFormData) {
    return {
      ...initialFormData,
      fullName: this.getTravelerName(),
      gender: getInitialToggleValue(travelerFormOptions.GENDER, this.gender),
      dateOfBirth: this.dateOfBirth,
      isDOBValid: !dateOfBirth(this.dateOfBirth, initialFormData),
      requiredDateOfBirth: true,
      paxType: this.paxType,
      corporateFrequentFlyerProgram: this.corporateFrequentFlyerProgram,
      frequentFlyerProgram: this.frequentFlyerProgram,
      frequentFlyerNumber: this.frequentFlyerNumber,
      corporateFrequentFlyerNumber: this.corporateFrequentFlyerNumber,
      redress: this.redressNumber,
      ktn: this.ktn,
      requiresUpdateProfile: this.requiresUpdateProfile,
      nickname: this.nickname,
      benefitRedeemed: this.benefitRedeemed,
      benefitRedeemedCollection: this.benefitRedeemedCollection,
      mealSelectionAllowed: this.mealSelectionAllowed,
    };
  }

  getShortName() {
    return `${this.firstName[0]}${this.lastName[0]}`;
  }

  /*
   * @param type index
   * @return SegmentChoice for position in segmentsChoiceCollection
  */
  getSeat(index) {
    const segmentChoice = this.getSegmentChoice(index);
    return segmentChoice !== null && segmentChoice !== undefined ? segmentChoice.seat : null;
  }

  /*
   * @param segmentCode
   * @return SegmentChoice for segmentCode
  */
  getSeatBySegment(code) {
    const segmentChoice = this.getSegmentChoiceBySegment(code);
    return segmentChoice !== null && segmentChoice !== undefined ? segmentChoice.seat : null;
  }

  getSegmentChoice(index) {
    return this.segmentChoices !== null ? this.segmentChoices.getSegmentChoice(index) : null;
  }

  getSegmentChoiceBySegment(code) {
    return this.segmentChoices !== null ? this.segmentChoices.getSegmentChoiceBySegment(code) : null;
  }

  getUnPaidSegmentChoice(segments) {
    return this.segmentChoices  === null ? [] : this.segmentChoices.getUnPaidSegmentChoice(segments);
  }

  getTicketedSegmentChoice(segments) {
    return this.segmentChoices  === null ? [] : this.segmentChoices.getTicketedSegmentChoice(segments);
  }

  getTravelerAgeInMonthsByFlight(dateOfFlight) {
    return Moment(dateOfFlight).diff(Moment(this.dateOfBirth, 'YYYY-MM-DD'), 'months');
  }

  getAllUnPaidSegmentChoice(isOnHold = false) {
    return this.segmentChoices  === null ? [] : this.segmentChoices.getAllUnPaidSegmentChoice(isOnHold);
  }

  getAllPaidSegmentChoice(isOnHold = false) {
    return this.segmentChoices  === null ? [] : this.segmentChoices.getAllPaidSegmentChoice(isOnHold);
  }

  getBaggageUnPaidAncillaries() {
    return this.ancillaries.getBaggageUnPaidAncillaries();
  }

  getCarryOnPaidAncillaries() {
    const arrayCarryOnPaid = [];

    this.freeBaggageAllowancePerLegsArray.filter(travelerAncillary => {
      const legCode = travelerAncillary?.legCode;
      const freeBaggageAllowance = travelerAncillary?.freeBaggageAllowance;
      const carryOn = freeBaggageAllowance && freeBaggageAllowance?.carryOn;
      const carryOnWeightCollection = freeBaggageAllowance && freeBaggageAllowance?.carryOnWeightCollection;

      if (carryOn && carryOn > 0) {
        arrayCarryOnPaid.push({
          legCode: legCode,
          weight: carryOnWeightCollection,
          quantity: carryOn,
        });
      }
    });

    return arrayCarryOnPaid;
  }

  getBaggagePaidAncillaries() {
    const arrayBaggagePaid = [];
    const hasInfant = this.infant !== null;

    this.freeBaggageAllowancePerLegsArray.filter(travelerAncillary => {
      const legCode = travelerAncillary?.legCode;
      const freeBaggageAllowance = travelerAncillary?.freeBaggageAllowance;
      const checkedWeightCollection = freeBaggageAllowance && freeBaggageAllowance?.checkedWeightCollection;
      const bag20 = checkedWeightCollection?.[0]?.checkedFreeWeight === 20;
      const bag25 = checkedWeightCollection?.[0]?.checkedFreeWeight === 25;
      const checked = freeBaggageAllowance && freeBaggageAllowance?.checked;
      const isBagPremier = checkedWeightCollection?.[0]?.checkedFreeWeight === 32;
      const addBagInfantInPremier = hasInfant && isBagPremier && checked === 3;
      const baggageWeightCollection25 = [
        { checkedFreeWeight: 55, checkedMaxWeight: 99, checkedWeightUnit: 'LB' },
        { checkedFreeWeight: 25, checkedMaxWeight: 45, checkedWeightUnit: 'KG' },
      ];

      if (checked && checked > 0) {
        if (bag20) {
          arrayBaggagePaid.push({
            legCode: legCode,
            weight: checkedWeightCollection,
            quantity: checked,
          });
        } else if (bag25 || addBagInfantInPremier) {
          arrayBaggagePaid.push({
            legCode: legCode,
            weight: baggageWeightCollection25,
            quantity: !addBagInfantInPremier ? checked : 1,
          });
        }
      }
    });

    return arrayBaggagePaid;
  }

  getExtraBaggagePaidAncillaries() {
    const arrayExtraBaggagePaid = [];
    const hasInfant = this.infant !== null;

    this.freeBaggageAllowancePerLegsArray.filter(travelerAncillary => {
      const legCode = travelerAncillary?.legCode;
      const freeBaggageAllowance = travelerAncillary?.freeBaggageAllowance;
      const checkedWeightCollection = freeBaggageAllowance && freeBaggageAllowance?.checkedWeightCollection;
      const bag32 = checkedWeightCollection?.[0]?.checkedFreeWeight === 32;
      let checked = freeBaggageAllowance && freeBaggageAllowance?.checked;

      if (hasInfant) {
        if (checked === 3) {
          checked = 2;
        }
      }

      if (checked && checked > 0 && bag32) {
        arrayExtraBaggagePaid.push({
          legCode: legCode,
          weight: checkedWeightCollection,
          quantity: checked,
        });
      }
    });

    return arrayExtraBaggagePaid;
  }

  getBaggageUnPaidAncillariesPP() {
    return this.ancillaries.getBaggageUnPaidAncillariesPP();
  }

  getCarryOnUnPaidAncillaries() {
    return this.ancillaries.getCarryOnUnPaidAncillaries();
  }

  getCarryOnUnPaidAncillariesPP() {
    return this.ancillaries.getCarryOnUnPaidAncillariesPP();
  }

  getBaggageTicketedAncillaries() {
    return this.ancillaries.getBaggageTicketedAncillaries();
  }

  getCarryOnTicketedAncillaries() {
    return this.ancillaries.getCarryOnTicketedAncillaries();
  }

  getSpecialBaggageUnPaidAncillaries() {
    return this.ancillaries.getSpecialBaggageUnPaidAncillaries();
  }

  getSpecialBaggageTicketedAncillaries() {
    return this.ancillaries.getSpecialBaggageTicketedAncillaries();
  }

  hasInfant() {
    return this.infant !== null;
  }

  getTravelerInitialsInfant() {
    return `${this.infant.firstName ? this.infant.firstName.charAt(0) : ''}${this.infant.lastName ? this.infant.lastName.charAt(0) : ''}`;
  }

  getGenderInfant() {
    return this.hasInfant() ? this.infant.gender : null;
  }

  getDateOfBirthInfant() {
    return this.hasInfant() ? this.infant.dateOfBirth : null;
  }

  getTicketNumberInfant() {
    return this.hasInfant() ? this.infant.ticketNumber : null;
  }

  getNameInfant() {
    return `${this.infant.firstName} ${this.infant.lastName}`;
  }

  getWeightBagByTravel(type, legCode) {
    const itemFreeBaggage = this.freeBaggageAllowancePerLegsArray?.find(item => item.legCode === legCode);
    const dataWeight = itemFreeBaggage && itemFreeBaggage.freeBaggageAllowance.checkedWeightCollection.find(item => item.checkedWeightUnit === type);
    return dataWeight ? dataWeight.checkedFreeWeight : null;
  }

  getWeightCarryOnByTravel(type, legCode) {
    const itemFreeBaggage = this.freeBaggageAllowancePerLegsArray?.find(item => item.legCode === legCode);
    return itemFreeBaggage ? type === 'KG' ? itemFreeBaggage.freeBaggageAllowance.carryOnWeightKG : itemFreeBaggage.freeBaggageAllowance.carryOnWeightLB : null;
  }

  /**
   * @return {String}
   */
  getTravelerInitials() {
    return `${this.firstName ? this.firstName.charAt(0) : ''}${this.lastName ? this.lastName.charAt(0) : ''}`;
  }

  /**
   * @return {String}
   */
  getTravelerName() {
    if (!this.firstName || !this.lastName) { return ''; }

    return `${this.firstName} ${this.lastName}`;
  }

  getBenefitLevelSeat() {
    return this.benefit ? this.benefit._collection[0].code : '';
  }

  getTypeBenefit() {
    const benefit = this.benefit ? this.benefit._collection[0].code.includes('DELTA') : null;
    return benefit !== null ?  benefit ? 'DL' : 'AM' : '';
  }

  getPaidCBX(legCodesAvailableMYB) {
    return this.ancillaries.ancillaries.filter(travelerAncillary => {
      return travelerAncillary && travelerAncillary.type === '029' && travelerAncillary.isPaid && legCodesAvailableMYB && legCodesAvailableMYB.includes(travelerAncillary.legCode);
    });
  }

  getQRCodeCBX(legCodesAvailableMYB) {
    return this.ancillaries.ancillaries.filter(travelerAncillary => {
      return travelerAncillary && travelerAncillary.type === '029' && travelerAncillary.qrcode && legCodesAvailableMYB && legCodesAvailableMYB.includes(travelerAncillary.legCode);
    });
  }

  hasBagCorporateRedeemed() {
    return this.ancillaries.hasBagCorporateRedeemed();
  }

  hasBagCorporateRedeemedByLegCode(legCode) {
    return this.ancillaries.hasBagCorporateRedeemedByLegCode(legCode);
  }

  hasTicketedUpgrade() {
    return this.ancillaries.hasTicketedUpgrade();
  }

  hasUnpaidAncillary() {
    if (!this.ancillaries || 
        !this.ancillaries.ancillaries || 
        !Array.isArray(this.ancillaries.ancillaries)) {
      return false;
    }
    
    return this.ancillaries.ancillaries.some(travelerAncillary => {
      // Ancillary directo sin pagar
      if (travelerAncillary?.ancillary && 
          travelerAncillary.ancillary.redeemedCP === false && 
          travelerAncillary.ancillary.redeemed === false) {
        return true;
      }
      
      // Ancillary dentro de offer sin pagar
      if (travelerAncillary?.ancillary?.ancillaryOffer?.ancillary &&
          travelerAncillary.ancillary.ancillaryOffer.ancillary.redeemedCP === false) {
        return true;
      }
      
      return false;
    });
  }

  hasUnpaidAncillaryPP() {
    if (!this.ancillaries || 
        !this.ancillaries.ancillaries || 
        !Array.isArray(this.ancillaries.ancillaries)) {
      return false;
    }
    
    return this.ancillaries.ancillaries.some(travelerAncillary => {
      // Ancillary directo con redeemedCP
      if (travelerAncillary?.ancillary && travelerAncillary.ancillary.redeemedCP) {
        return true;
      }
      
      // Ancillary dentro de offer con redeemedCP
      if (travelerAncillary?.ancillary?.ancillaryOffer?.ancillary &&
          travelerAncillary.ancillary.ancillaryOffer.ancillary.redeemedCP) {
        return true;
      }
      
      return false;
    });
  }

  hasUnpaidBaggagePP() {
    return !!this.ancillaries.ancillaries.find(travelerAncillary => {
      return travelerAncillary.ancillary && travelerAncillary.ancillary.redeemedCP && travelerAncillary.getGroupCode() === 'BG';
    });
  }

  hasUnpaidCarryOnPP() {
    return !!this.ancillaries.ancillaries.find(travelerAncillary => {
      return travelerAncillary.ancillary && travelerAncillary.ancillary.redeemedCP && travelerAncillary.getGroupCode() === 'TS' && travelerAncillary.getType() === '07X';
    });
  }

  hasUnpaidFlyGreen() {
    return this.ancillaries?.ancillaries.some(travelerAncillary => {
      return travelerAncillary?.ancillary?.redeemed === false && travelerAncillary?.ancillary?.type === '09T';
    });
  }

  hasUnpaidPriority() {
    return !!this.ancillaries.ancillaries.find(travelerAncillary => {
      return travelerAncillary.ancillary && travelerAncillary.ancillary.redeemed === false && travelerAncillary.ancillary.type === 'PRI';
    });
  }

  hasUnpaidUpgrade() {
    return !!this.ancillaries.ancillaries.find(travelerAncillary => {
      return travelerAncillary.ancillary && travelerAncillary.ancillary.ancillaryOffer && travelerAncillary.ancillary.ancillaryOffer.ancillary && travelerAncillary.ancillary.ancillaryOffer.ancillary.redeemedCP === false;
    });
  }

  hasUnpaidUpgradePP() {
    return !!this.ancillaries.ancillaries.find(travelerAncillary => {
      return travelerAncillary.ancillary && travelerAncillary.ancillary.ancillaryOffer && travelerAncillary.ancillary.ancillaryOffer.ancillary && travelerAncillary.ancillary.ancillaryOffer.ancillary.redeemedCP;
    });
  }

  hasTicketedAncillary() {
    return this.ancillaries.hasSomeTicketedAncillary;
  }

  hasTicketedAncillaryPP() {
    return this.ancillaries.hasSomeTicketedAncillaryPP;
  }

  hasSeatBenefit() {
    return this.benefit?._collection?.[0]?.seatBenefits || null;
  }

  hasUnpaidSeat() {
    let redimir = false;
    if (this.segmentChoices === null || this.hasSeatBenefit() !== null) {
      return redimir;
    } else {
      if (this.segmentChoices.items && this.segmentChoices.items.length !== 0) {
        this.segmentChoices.items.forEach(item => {
          if (!item.redeemedCP && !item.isPaid) {
            redimir = this.segmentChoices.hasSomeUnpaidSeat;
            if (item.seat.currency && !item.seat.currency.total && item.seat.currency.total === 0) {
              redimir = false;
            }
          }
        });
        return redimir;
      }
    }
  }

  hasUnpaidSeatPP() {
    let redimir = false;
    if (this.segmentChoices && this.segmentChoices.items && this.segmentChoices.items.length !== 0) {
      this.segmentChoices.items.forEach(item => {
        if (item.redeemedCP && !item.isPaid) {
          redimir = true;
        }
      });
      return redimir;
    }
  }

  hasTicketedSeat() {
    return this.segmentChoices === null ? false : this.segmentChoices.hasSomeTicketedSeat;
  }

  hasTicketedSeatPP() {
    return this.segmentChoices === null ? false : this.segmentChoices.hasSomeTicketedSeatPP;
  }

  hasSeatBySegment(code, index) {
    return this.segmentChoices === null ? false : this.segmentChoices[index] === undefined ?  false : this.segmentChoices[index].isPaid ? this.segmentChoices[index].hasSeatByCode(code) : false;
  }

  hasInfant() {
    return this.infant !== null;
  }

  isInsuranceAvailableForTraveler(departureDateLastSegment, storefront) {
    return isValidAgeForInsurance(this.getTravelerAgeInMonthsByFlight(departureDateLastSegment), storefront);
  }

  updateSegmentChoiceBySegmentCode(travelerData) {
    const {
      segmentCode,
      choice,
      seatSelectionFee,
      redeemedCP,
      beneficiaryTier,
    } = travelerData;

    if (beneficiaryTier) {
      this.updateBeneficiaryTier(beneficiaryTier);
    }

    const dataSegmentChoice = this.segmentChoices ? this.segmentChoices.getSegmentChoiceBySegmentCode(segmentCode) : null;
    const newSegmentChoice = {
      'fareType': null,
      'isGenericSeat': choice.isGeneric,
      'newSeat': false,
      'redeemedCP': redeemedCP,
      'seat': choice,
      'seatSelectionFee': (!choice.currency && seatSelectionFee) ? seatSelectionFee : null,
      'segmentCode': segmentCode,
      'status': null,
      '_meta': {
        'class': SEGMENT_TYPES.SEGMENT_CHOICE,
      },
    };

    if (this.segmentChoices) {
      const segmentChoice = new SegmentChoiceUIModel(newSegmentChoice);
      if (dataSegmentChoice) {
        this.segmentChoices.items[dataSegmentChoice.index] = segmentChoice;
      } else {
        this.segmentChoices.items[this.segmentChoices.items.length] = segmentChoice;
      }
    } else {
      const segmentChoices = {
        '_collection': [newSegmentChoice],
      };
      this.segmentChoices = new SegmentChoiceCollectionUIModel(segmentChoices);
    }
  }

  removeSegmentChoiceBySegmentCode(segmentCode) {
    const dataSegmentChoice = this.segmentChoices ? this.segmentChoices.getSegmentChoiceBySegmentCode(segmentCode) : null;
    if (this.segmentChoices && this.segmentChoices.items && this.segmentChoices.items.length !== 0 && dataSegmentChoice) {
      this.segmentChoices.items.splice(dataSegmentChoice.index, 1);
    }
  }

  getBaggageAncillary() {
    return this.ancillaries.getBaggageAncillary();
  }

  getSpecialBaggageAncillary() {
    return this.ancillaries.getSpecialBaggageAncillary();
  }

  getSpecialBaggageAncillaryByLegCode(legCode) {
    return this.ancillaries.getSpecialBaggageAncillaryByLegCode(legCode);
  }

  getBaggageAncillaryByLegCode(legCode) {
    return this.ancillaries.getBaggageAncillaryByLegCode(legCode);
  }

  getPaidBaggageAncillaryByLegCode(legCode) {
    return this.ancillaries.getPaidBaggageAncillaryByLegCode(legCode);
  }

  getPaidCarryOnAncillaryByLegCode(legCode) {
    return this.ancillaries.getPaidCarryOnAncillaryByLegCode(legCode);
  }

  getPaidSpecialBaggageAncillaryByLegCode(legCode) {
    return this.ancillaries.getPaidSpecialBaggageAncillaryByLegCode(legCode);
  }

  getUnpaidBaggageAncillaryByLegCode(legCode) {
    return this.ancillaries.getUnpaidBaggageAncillaryByLegCode(legCode);
  }

  getUnpaidCarryOnAncillaryByLegCode(legCode) {
    return this.ancillaries.getUnpaidCarryOnAncillaryByLegCode(legCode);
  }

  getUnpaidSpecialBaggageAncillaryByLegCode(legCode) {
    return this.ancillaries.getUnpaidSpecialBaggageAncillaryByLegCode(legCode);
  }

  getAncillaryBuilderInstance() {
    return this.ancillaries.getAncillaryBuilderInstance();
  }

  getTotalBaggageByLegCode(legCode) {
    const baggageAncillary = this.getBaggageAncillaryByLegCode(legCode);
    let freeBaggage = 0;
    if (this.freeBaggageAllowancePerLegsArray) {
      this.freeBaggageAllowancePerLegsArray.forEach(item => {
        if (item.legCode === legCode) {
          freeBaggage = item.freeBaggageAllowance.checked;
        }
      });
    }
    return baggageAncillary.length + freeBaggage;
  }

  getTotalFreeBaggage() {
    let freeBaggage = 0;
    if (this?.freeBaggageAllowancePerLegsArray?.length > 0) {
      this.freeBaggageAllowancePerLegsArray.map(freeBaggageAllowancePerLeg => {
        const freeBaggageChecked = freeBaggageAllowancePerLeg.freeBaggageAllowance?.checked;
        if (freeBaggageChecked > 0) {
          freeBaggage = freeBaggage + freeBaggageChecked;
        }
      });
    } else {
      if (this?.freeBaggageAllowance?.checked > 0) {
        freeBaggage = freeBaggage + this.freeBaggageAllowance.checked;
      }
    }

    return freeBaggage;
  }

  getTotalFreeBaggageByLegCode(legCode) {
    let freeBaggage = 0;
    const hasInfant = this.infant !== null;

    if (this.freeBaggageAllowancePerLegsArray && this.freeBaggageAllowancePerLegsArray.length > 0) {
      const hasFreeBaggagePerLeg = this.freeBaggageAllowancePerLegsArray.find(freeBaggageAllowance => {
        return freeBaggageAllowance.legCode === legCode;
      });

      if (hasFreeBaggagePerLeg) {
        freeBaggage =  hasFreeBaggagePerLeg?.freeBaggageAllowance?.checked;

        // validacion para pasajeros con infantes y con 7 maletas
        if (hasInfant) {
          if (freeBaggage > 0) {
            freeBaggage = freeBaggage - 1;
          }
        }
      } else {
        // Validacion para codeShare
        if (this.freeBaggageAllowance.checked > 0) {
          freeBaggage = this.freeBaggageAllowance.checked;
        }
      }
    } else {
      // Validacion para codeShare
      if (this.freeBaggageAllowance.checked > 0) {
        freeBaggage = this.freeBaggageAllowance.checked;
      }
    }

    return freeBaggage;
  }

  getTotalCarryOnByLegCode(legCode) {
    let carryOn = 0;
    if (this.freeBaggageAllowancePerLegsArray && this.freeBaggageAllowancePerLegsArray.length > 0) {
      this.freeBaggageAllowancePerLegsArray.forEach(item => {
        if (item.legCode === legCode) {
          carryOn = item.freeBaggageAllowance.carryOn;
        }
      });
    } else {
      if (this.freeBaggageAllowance.carryOn > 0) {
        carryOn = this.freeBaggageAllowance.carryOn;
      }
    }
    return carryOn;
  }

  getTotalPaidBaggageByLegCode(legCode) {
    return this.getPaidBaggageAncillaryByLegCode(legCode).length;
  }

  getTotalPaidCarryOnByLegCode(legCode) {
    return this.getPaidCarryOnAncillaryByLegCode(legCode).length;
  }

  getTotalRedeemedBaggageByLegCode(legCode) {
    return this.ancillaries.getUnpaidRedeemedBaggageAncillaryByLegCode(legCode).length;
  }

  getIsRedeemedCPBaggageByLegCode(legCode) {
    return this.ancillaries.getUnpaidRedeemedCPBaggageAncillaryByLegCode(legCode).length > 0;
  }

  getTotalSpecialPaidBaggageByLegCode(legCode) {
    return this.getPaidSpecialBaggageAncillaryByLegCode(legCode).length;
  }

  getTotalUnpaidBaggageByLegCode(legCode) {
    return this.getUnpaidBaggageAncillaryByLegCode(legCode).length;
  }

  getTotalUnpaidCarryOnByLegCode(legCode) {
    return this.getUnpaidCarryOnAncillaryByLegCode(legCode).length;
  }

  getTotalSpecialUnpaidBaggageByLegCode(legCode) {
    return this.getUnpaidSpecialBaggageAncillaryByLegCode(legCode).length;
  }

  /*
   * @param {Array} ancillaries
   * @param {String} legCode
  */
  updateAncillaryCollection(ancillaries, legCode, isCarryOn = false) {
    this.ancillaries.updateAncillaries(ancillaries, legCode, isCarryOn);
  }

  updateBeneficiaryTier(beneficiaryTier) {
    this.beneficiaryTier = beneficiaryTier;
    this.isRedemptionBeneficiaryTier = true;
  }

  updateTravelerData({
    gender = this.gender,
    dateOfBirth = this.dateOfBirth,
    corporateFrequentFlyerNumber = this.corporateFrequentFlyerNumber,
    corporateFrequentFlyerProgram = this.corporateFrequentFlyerProgram,
    frequentFlyerNumber = this.frequentFlyerNumber,
    frequentFlyerProgram = this.frequentFlyerProgram,
    ktn = this.ktn,
    redress = this.redress,
    specialServicesRequired = false,
  } = {}) {
    this.gender = gender ? gender.toggle : null;
    this.dateOfBirth = dateOfBirth;
    this.corporateFrequentFlyerNumber = corporateFrequentFlyerNumber;
    this.corporateFrequentFlyerProgram = corporateFrequentFlyerProgram;
    this.frequentFlyerNumber = frequentFlyerNumber;
    this.frequentFlyerProgram = frequentFlyerProgram;
    this.ktn = ktn;
    this.redressNumber = redress;
    this.ssrRemarks.updateTravelerSSRRemarks(specialServicesRequired);
  }

  setManageUserProfileAccount(userProfileAccount) {
    this.ManageUserProfileAccount = new UserProfileAccountUIModel(userProfileAccount);
  }

  setUserIdCorporate(userId) {
    this.userId = userId;
  }

  setConcurFFNumber(ffNumber) {
    this.concurFFNumber = ffNumber;
  }

  toApi() {
    return {
      '_meta':{
        'class':'BookedTraveler',
      },
      'ancillaries': this.ancillaries.toApi(),
      'beneficiaryTier': this.beneficiaryTier,
      'benefitRedeemedCollection': this.benefitRedeemedCollection,
      'bookingClasses': this.bookingClasses,
      'checkinStatus':this.checkinStatus,
      'corporateFrequentFlyerNumber': this.corporateFrequentFlyerNumber,
      'corporateFrequentFlyerProgram': this.corporateFrequentFlyerProgram,
      'countryOfResidence': this.countryOfResidence,
      'dateOfBirth': this.dateOfBirth,
      'declinedEmergencyContact': this.declinedEmergencyContact,
      'destinationAddress': this.destinationAddress,
      'email': this.email,
      'emergencyContact': this.emergencyContact,
      'firstName': this.firstName,
      'frequentFlyerNumber': this.frequentFlyerNumber,
      'frequentFlyerProgram': this.frequentFlyerProgram,
      'gender': this.gender,
      'id': this.id,
      'infant': this.infant,
      'isEligibleToCheckin': this.isEligibleToCheckin,
      'isRedemptionBeneficiaryTier': this.isRedemptionBeneficiaryTier,
      'isSelectedToCheckin': this.isSelectedToCheckin,
      'ktn': this.ktn,
      'lastName': this.lastName,
      'middleName': this.middleName,
      'nameRefNumber': this.nameRefNumber,
      'onStandByList': this.onStandByList,
      'paxType': this.paxType,
      'phones': this.phones,
      'redressNumber': this.redressNumber,
      'ssrRemarks': this.ssrRemarks.toApi(),
      'segmentChoices': {
        '_meta':{
          'class':'BookedSegmentChoiceCollection',
        },
        '_collection': this.segmentChoices ? this.segmentChoices.toApi() : null,
      },
      'concurFFNumber': this.concurFFNumber,
      'ManageUserProfileAccount': this.ManageUserProfileAccount ? this.ManageUserProfileAccount.toApi() : null,
      'ticketNumber': this.ticketNumber,
      'token': this.token,
      'userId': this.userId,
    };
  }

  applyMealsPreselect() {
    return this.mealSelectionAllowed && this.mealSelectionAllowed.length ? true : false;
  }

  updateMealSelected(mealsSelected) {
    const mealsSelectedItems = [];
    mealsSelected.forEach( mealSelected => {
      const mealItem = {
        _collection: [mealSelected],
      };
      mealsSelectedItems.push(mealItem);
    });
    this.mealSelected = new MealsSelectedCollectionUIModel(mealsSelectedItems);
  }

  addUpgradeAncillary(ancillary, legCodesArray = null, isRedeemedCP = false, checkSegments) {
    if (legCodesArray) {
      legCodesArray.forEach(legCode => {
        this.ancillaries.addAncillary(ancillary, legCode, isRedeemedCP, checkSegments);
      });
    } else {
      this.ancillaries.addAncillary(ancillary, null, isRedeemedCP, checkSegments);
    }
  }

  addAncillary(ancillary, legCodesArray = null, isRedeemedCP = false) {
    if (legCodesArray) {
      legCodesArray.forEach(legCode => {
        this.ancillaries.addAncillary(ancillary, legCode, isRedeemedCP);
      });
    } else {
      this.ancillaries.addAncillary(ancillary, null, isRedeemedCP);
    }
  }

  removeUpgradeAncillary(ancillary) {
    this.ancillaries.removeUpgradeAncillary(ancillary);
  }

  removePriorityAncillary(ancillary) {
    this.ancillaries.removePriorityAncillary(ancillary);
  }

  removeAncillary(ancillaryType, legCode) {
    this.ancillaries.removeAncillary(ancillaryType, legCode);
  }

  getBenefitSeats() {
    return this.benefit ? this.benefit?._collection[0]?.seatBenefits?.feeRequired : null;
  }

  hasBenefitPreferedCP() {
    return this.benefit !== null;
  }

  setToken(token) {
    this.token = token;
  }
}

export default TravelerInfoUIModel;
